import { breakpoints } from '@/constants/breakpoints';
import useMediaQuery from '@/hooks/useMediaQuery';
import { type PaginationItemType } from '../components/PaginationItem';

interface Props {
  current: number;
  total: number;
}

const usePagination = ({ current, total }: Props): PaginationItemType[] => {
  const isTabletOrAbove = useMediaQuery(`(min-width: ${breakpoints.TABLET}px)`);
  const paginationItems: PaginationItemType[] = [
    {
      id: 'nav-prev',
      type: 'nav',
      direction: 'prev',
      page: current - 1,
      isDisabled: current <= 1,
    },
  ];

  const pagesToShowOnEachSide = isTabletOrAbove ? 2 : 1;
  const firstVisiblePage = Math.max(current - pagesToShowOnEachSide, 2); // not page 1 since it is always displayed
  const lastVisiblePage = Math.min(current + pagesToShowOnEachSide, total - 1); // not last page since it is always displayed

  paginationItems.push({
    id: 'page-1',
    type: 'page',
    page: 1,
  });

  if (firstVisiblePage > 2) {
    paginationItems.push({
      id: 'jumper-prev',
      type: 'jumper',
    });
  }

  for (let i = firstVisiblePage; i <= lastVisiblePage; i++) {
    paginationItems.push({
      id: `page-${i}`,
      type: 'page',
      page: i,
    });
  }

  if (lastVisiblePage < total - 1) {
    paginationItems.push({
      id: 'jumper-next',
      type: 'jumper',
    });
  }

  paginationItems.push({
    id: `page-${total}`,
    type: 'page',
    page: total,
  });

  paginationItems.push({
    id: 'nav-next',
    type: 'nav',
    direction: 'next',
    page: current + 1,
    isDisabled: current === total,
  });

  return paginationItems;
};

export default usePagination;
